import { NotFoundError } from "../../../../../../utils/error";
import { UrlFilters } from "../../urlFilter";
import { FilterMap, ISelectedFilter, ISelectedFilterGroup, ISelectedFilterMap } from "../types";

const parseSecondaryFilter = (
  filterMap: FilterMap,
  { key, value }: { key: string; value: string }
): ISelectedFilter | null => {
  const entityFilter = filterMap[key];
  const filterFound = key in filterMap && entityFilter && value in entityFilter.filters;
  return filterFound
    ? {
        entityFilter: entityFilter.entityFilter,
        filter: entityFilter.filters[value]
      }
    : null;
};

const parseSecondaryFilters = (
  filterMap: FilterMap,
  secondaryFilters: UrlFilters["secondary"]
): ISelectedFilterGroup =>
  Object.keys(secondaryFilters).reduce((acum, key) => {
    const parsedFilter = parseSecondaryFilter(filterMap, {
      key,
      value: secondaryFilters[key]
    });
    return parsedFilter ? { ...acum, [parsedFilter.entityFilter.entity.slug]: parsedFilter } : acum;
  }, {});

const featuredMatchEntityFilter = (featuredSlug: string) => (entityFilterSlug: string): boolean => {
  const entityFilterRegex = new RegExp(`^${entityFilterSlug}-(.*)`);
  return Boolean(featuredSlug.match(entityFilterRegex));
};

const featuredMatchFullFilter = (featuredSlug: string, entityFilterSlug: string) => (
  filterSlug: string
): boolean => {
  const filterRegex = new RegExp(`^${entityFilterSlug}-${filterSlug}$`);
  return Boolean(featuredSlug.match(filterRegex));
};

const parseFeaturedFilter = (
  filterMap: FilterMap,
  featuredFilter: string
): ISelectedFilter | null => {
  const entityFilterSlug = Object.keys(filterMap).find(featuredMatchEntityFilter(featuredFilter));
  if (entityFilterSlug) {
    const filterSlug = Object.keys(filterMap[entityFilterSlug].filters).find(
      featuredMatchFullFilter(featuredFilter, entityFilterSlug)
    );
    if (filterSlug) {
      return {
        entityFilter: filterMap[entityFilterSlug].entityFilter,
        filter: filterMap[entityFilterSlug].filters[filterSlug]
      };
    }
  }
  throw new NotFoundError();
};

const parseFeaturedFilters = (
  filterMap: FilterMap,
  featuredFilters: UrlFilters["featured"]
): ISelectedFilterGroup =>
  featuredFilters
    .map(filterSlug => parseFeaturedFilter(filterMap, filterSlug))
    .filter(parsedFilter => parsedFilter !== null)
    .reduce<ISelectedFilterGroup>(
      (acum, parsedFilter) => ({
        ...acum,
        [parsedFilter!.entityFilter.entity.slug]: parsedFilter!
      }),
      {}
    );

export const selectedFilterParser = (
  filterMap: FilterMap,
  { featured, secondary }: UrlFilters
): ISelectedFilterMap => ({
  featured: parseFeaturedFilters(filterMap, featured),
  secondary: parseSecondaryFilters(filterMap, secondary)
});
